import { createSlice, CaseReducer, PayloadAction } from '@reduxjs/toolkit';
import { Toast } from '@10d/tend-ui/primitives/Toast';

import { get, patch, post } from 'api/requests';

import { AppThunk } from 'store/types';
import {
  IParamsReq,
  ISubmitValues,
  IHistoryPlan,
  IHistoryReqParams,
  IGetOvpPlansparams,
  IAllPlanParams,
  IPlanOvp,
  IStructureDates,
  IEditPlanParams,
  IWorkPlan,
  IHistoryChanges,
  IHistoryChangesByIds,
} from 'types';
import {
  IPlanHistoryCurrent,
  IPlanList,
  IPlanReadonly,
  IPlansPlacementsStore,
  IRightDates,
} from 'features/Plan/PlanPlacements/types';
import { PlanEdit } from 'features/Plan/PlanEngineering/types';

const initialState: IPlansPlacementsStore = {
  list: [],
  loading: false,
  count: 0,
  current: {} as PlanEdit,
  currentHistory: {} as IPlanHistoryCurrent,
  readonlyPlans: {} as IPlanReadonly,
  historyProjectPlan: [] as IHistoryPlan[],
  rightDates: [],
  planOvp: {} as IPlanOvp,
  structureDates: {} as IStructureDates,
  historyChanges: {} as IHistoryChangesByIds,
};

const reset: CaseReducer<IPlansPlacementsStore> = () => initialState;

const loading: CaseReducer<IPlansPlacementsStore, PayloadAction<boolean>> = (state, action) => {
  state.loading = action.payload;
};

const current: CaseReducer<IPlansPlacementsStore, PayloadAction<PlanEdit>> = (state, action) => {
  state.current = action.payload;
};

const currentHistory: CaseReducer<IPlansPlacementsStore, PayloadAction<IPlanHistoryCurrent>> = (
  state,
  action,
) => {
  state.currentHistory = action.payload;
};

const count: CaseReducer<IPlansPlacementsStore, PayloadAction<number>> = (state, action) => {
  state.count = action.payload;
};

const list: CaseReducer<IPlansPlacementsStore, PayloadAction<IPlanList[]>> = (state, action) => {
  state.list = action.payload;
};

const readonlyPlans: CaseReducer<IPlansPlacementsStore, PayloadAction<IPlanReadonly>> = (
  state,
  action,
) => {
  state.readonlyPlans = action.payload;
};

const historyProjectPlan: CaseReducer<IPlansPlacementsStore, PayloadAction<IHistoryPlan[]>> = (
  state,
  action,
) => {
  state.historyProjectPlan = action.payload;
};

const planOvp: CaseReducer<IPlansPlacementsStore, PayloadAction<IPlanOvp>> = (state, action) => {
  state.planOvp = action.payload;
};

const structureDates: CaseReducer<IPlansPlacementsStore, PayloadAction<IStructureDates>> = (
  state,
  action,
) => {
  state.structureDates = action.payload;
};

const rightDates: CaseReducer<IPlansPlacementsStore, PayloadAction<IRightDates[]>> = (
  state,
  action,
) => {
  state.rightDates = action.payload;
};

const historyChanges: CaseReducer<IPlansPlacementsStore, PayloadAction<IHistoryChangesByIds>> = (
  state,
  { payload },
) => {
  state.historyChanges = {
    ...state.historyChanges,
    ...payload,
  };
};

export const planPlacementSlice = createSlice({
  name: 'planPlacement',
  initialState,
  reducers: {
    reset,
    loading,
    list,
    count,
    current,
    currentHistory,
    readonlyPlans,
    historyProjectPlan,
    planOvp,
    structureDates,
    rightDates,
    historyChanges,
  },
});

const {
  reset: setReset,
  loading: setLoading,
  list: setListPlans,
  count: setPlansCount,
  current: setCurrentPlans,
  currentHistory: setCurrentHistory,
  readonlyPlans: setReadonlyPlans,
  historyProjectPlan: setHistoryProjectPlan,
  planOvp: setOvpPlan,
  structureDates: setStructureDates,
  rightDates: setRightDates,
  historyChanges: setHistoryChanges,
} = planPlacementSlice.actions;

export const setPlans =
  (plans?: IPlanList[]): AppThunk =>
  dispatch => {
    dispatch(setListPlans(plans || []));
  };

export const getAllPlans =
  (params: IAllPlanParams): AppThunk =>
  dispatch => {
    dispatch(setLoading(true));

    get<IPlanReadonly>('/api/settlement/plan/view/', params)
      .then(({ data }) => {
        dispatch(setReadonlyPlans(data));
      })
      .finally(() => dispatch(setLoading(false)));
  };

export const getOvpPlan =
  (params: IGetOvpPlansparams): AppThunk =>
  dispatch => {
    dispatch(setLoading(true));

    get<IPlanOvp>('/api/settlement/plan/load/', params)
      .then(({ data }) => {
        dispatch(setOvpPlan(data));
      })
      .finally(() => dispatch(setLoading(false)));
  };

export const getAllStructurePlans =
  (params: IParamsReq): AppThunk =>
  dispatch => {
    dispatch(setLoading(true));

    get<Resp>('/api/settlement/plan/', params)
      .then(({ data }) => {
        const { results, count } = data;

        const changedResult = results.map(plan => {
          plan.expandable = true;
          return plan;
        });

        dispatch(setPlans(changedResult));
        dispatch(setPlansCount(count));
      })
      .finally(() => dispatch(setLoading(false)));
  };

type Resp = { results: IPlanList[]; count: number };

export const getEditPlan =
  (id: number, params?: IEditPlanParams): AppThunk =>
  dispatch => {
    dispatch(setLoading(true));
    get<PlanEdit>(`/api/settlement/plan/${id}/`, params)
      .then(({ data }) => {
        dispatch(setCurrentPlans(data));
      })
      .finally(() => dispatch(setLoading(false)));
  };

export const getHistoryPlan =
  (historyId: string): AppThunk =>
  dispatch => {
    dispatch(setLoading(true));

    get<IPlanHistoryCurrent>(`/api/settlement/plan/history/${historyId}/`)
      .then(({ data }) => {
        dispatch(setCurrentHistory(data));
      })
      .finally(() => dispatch(setLoading(false)));
  };

export const getHistoryProjectPlan =
  (params: IHistoryReqParams): AppThunk =>
  dispatch => {
    dispatch(setLoading(true));

    get<IHistoryPlan[]>('/api/settlement/plan/history/', params)
      .then(({ data }) => {
        dispatch(setHistoryProjectPlan(data));
      })
      .finally(() => dispatch(setLoading(false)));
  };

export const getStructureDates =
  (structure: number): AppThunk =>
  dispatch => {
    dispatch(setLoading(true));
    get<IStructureDates>(`/api/settlement/object/${structure}/dates/`)
      .then(({ data }) => {
        dispatch(setStructureDates(data));
      })
      .finally(() => dispatch(setLoading(false)));
  };

export const getRightDates = (): AppThunk => dispatch => {
  dispatch(setLoading(true));

  get<IRightDates[]>('/api/settlement/plan/right_dates/')
    .then(({ data }) => {
      dispatch(setRightDates(data));
    })
    .finally(() => dispatch(setLoading(false)));
};

export const getHistoryChanges =
  (id: number): AppThunk =>
  dispatch => {
    dispatch(setLoading(true));

    get<IHistoryChanges>(`/api/settlement/plan/${id}/history/`)
      .then(({ data }) => {
        dispatch(setHistoryChanges({ [id]: data }));
      })
      .finally(() => dispatch(setLoading(false)));
  };

export const createNewPlan =
  (values: ISubmitValues, callback: () => void): AppThunk =>
  dispatch => {
    dispatch(setLoading(true));

    post('/api/settlement/object/', values)
      .then(() => Toast.success({ message: 'План успешно создан' }))
      .then(() => callback())
      .finally(() => dispatch(setLoading(false)));
  };

export const agreementCalendarDraft =
  (calendarDraft: number, callback: () => void): AppThunk =>
  dispatch => {
    dispatch(setLoading(true));

    post('/api/settlement/plan/agreement_calendar_draft/', { calendarDraft })
      .then(() => Toast.success({ message: 'Черновик успешно согласован' }))
      .then(() => callback())
      .finally(() => dispatch(setLoading(false)));
  };

export const rejectCalendarDraft =
  (calendarDraft: number, callback: () => void): AppThunk =>
  dispatch => {
    dispatch(setLoading(true));

    post('/api/settlement/plan/reject_calendar_draft/', { calendarDraft })
      .then(() => Toast.success({ message: 'Черновик успешно отклонен' }))
      .then(() => callback())
      .finally(() => dispatch(setLoading(false)));
  };

export const changePlan =
  (
    _current: PlanEdit,
    id: number,
    workPlans: IWorkPlan[],
    createDraft = false,
    isRedirectNeeded = true,
    redirectHome: () => void,
  ): AppThunk =>
  dispatch => {
    dispatch(setLoading(true));
    patch(`/api/settlement/plan/${id}/`, { workPlans, createDraft })
      .then(() => {
        // result
        dispatch(getEditPlan(id));
        isRedirectNeeded && redirectHome();
        // TODO set patch response instead ger
        // dispatch(setCurrentPlans({ ...current, ...result }));
      })
      .then(() =>
        Toast.success({
          message: createDraft
            ? 'Изменения в плане отправлены на согласование'
            : 'План успешно сохранен',
        }),
      )
      .finally(() => dispatch(setLoading(false)));
  };

export { setReset, setCurrentPlans, setOvpPlan, setStructureDates };
